import React, { useState } from 'react';

const Pricing: React.FC = () => {
  const [openSection, setOpenSection] = useState<string | null>('tax-burden');
  const [annualTax, setAnnualTax] = useState<number>(5);

  const toggleSection = (section: string) => {
    setOpenSection(openSection === section ? null : section);
  };

  // シミュレーション計算
  const lawyerFee = 110;
  const calculateLoss = (years: number) => annualTax * years;
  const calculateNetCost = (years: number) => lawyerFee - calculateLoss(years);
  const breakEvenYears = Math.ceil(lawyerFee / annualTax);

  return (
    <section id="price" className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-6 lg:px-12">
        <div className="text-center mb-16">
          <p className="font-en text-navy-600 text-sm tracking-widest mb-4 uppercase">Price</p>
          <h2 className="font-noto text-3xl md:text-4xl font-bold text-navy-900 mb-4">
            料金表
          </h2>
        </div>
        
        {/* 基本料金表 */}
        <div className="bg-white rounded-lg border border-beige-200 p-8 mb-8">
          <div className="grid md:grid-cols-2 gap-8 mb-6">
            <div className="text-center">
              <div className="bg-navy-50 rounded-lg p-6 mb-4 border border-navy-200">
                <h3 className="font-noto text-base font-bold text-navy-800 mb-2">着手金</h3>
                <div className="font-en text-2xl font-bold text-navy-700">0円</div>
                <div className="font-noto text-xs text-navy-600">（受任時）</div>
              </div>
            </div>
            
            <div className="text-center">
              <div className="bg-navy-50 rounded-lg p-6 mb-4 border border-navy-200">
                <h3 className="font-noto text-base font-bold text-navy-800 mb-2">成功報酬</h3>
                <div className="font-en text-2xl font-bold text-navy-700">110万円</div>
                <div className="font-noto text-xs text-navy-600">（登記完了時）</div>
              </div>
            </div>
          </div>
          
          <div className="space-y-3 font-noto text-navy-700 text-sm mb-8">
            <div className="flex items-start gap-3">
              <i className="ri-information-line text-navy-600 mt-1"></i>
              <span>※隣接する3筆までが基本料金です</span>
            </div>
            
            <div className="flex items-start gap-3">
              <i className="ri-map-pin-line text-navy-600 mt-1"></i>
              <span>※近隣（2km以内）の土地は8割の料金で追加対応</span>
            </div>
            
            <div className="flex items-start gap-3">
              <i className="ri-money-yen-circle-line text-navy-600 mt-1"></i>
              <span>※実費（登記費用、測量費用など）は別途必要となります</span>
            </div>
          </div>

          {/* 料金詳細 */}
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-green-50 rounded-lg p-6 border border-green-200">
              <div className="flex items-center gap-2 mb-4">
                <i className="ri-check-double-line text-green-600 text-xl"></i>
                <h4 className="font-noto text-base font-bold text-green-800">料金に含まれるもの</h4>
              </div>
              <ul className="space-y-2 font-noto text-navy-700 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-green-600 mt-1">•</span>
                  <span>弁護士による調査・方針立案</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 mt-1">•</span>
                  <span>近隣・行政・譲受希望者との交渉</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 mt-1">•</span>
                  <span>契約書案の作成</span>
                </li>
              </ul>
            </div>

            <div className="bg-orange-50 rounded-lg p-6 border border-orange-200">
              <div className="flex items-center gap-2 mb-4">
                <i className="ri-file-list-line text-orange-600 text-xl"></i>
                <h4 className="font-noto text-base font-bold text-orange-800">別途発生しうる費用</h4>
              </div>
              <ul className="space-y-2 font-noto text-navy-700 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-orange-600 mt-1">•</span>
                  <span>測量・境界確定・取り壊し費用（必要な場合）</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-orange-600 mt-1">•</span>
                  <span>登記実費</span>
                </li>
              </ul>
            </div>

            <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
              <div className="flex items-center gap-2 mb-4">
                <i className="ri-shield-check-line text-blue-600 text-xl"></i>
                <h4 className="font-noto text-base font-bold text-blue-800">処分に至らなかった場合</h4>
              </div>
              <p className="font-noto text-navy-700 text-sm">
                成功報酬は発生せず、実費と着手金のみのご負担となります。
              </p>
            </div>
          </div>
        </div>

        {/* コスト分析セクション */}
        <div className="bg-white rounded-lg border border-beige-200 overflow-hidden">
          <div className="p-6 border-b border-beige-200">
            <h3 className="font-noto text-2xl font-bold text-navy-900 mb-2">
              このまま持ち続けると、いくら損をするのか？
            </h3>
            <p className="font-noto text-navy-700 text-sm">
              「110万円は高い」と感じられるかもしれません。しかし、負動産を持ち続けるコストを計算すると、それ以上の負担が発生する可能性が見えてきます。
            </p>
          </div>

          <div className="divide-y divide-beige-200">
            {/* ① 固定資産税の累積負担 */}
            <div>
              <button
                onClick={() => toggleSection('tax-burden')}
                className="w-full p-6 text-left hover:bg-beige-50 transition-colors duration-200 cursor-pointer"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <span className="bg-navy-800 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3">①</span>
                    <div>
                      <h4 className="font-noto text-xl font-bold text-navy-900">
                        固定資産税の累積負担
                      </h4>
                      <p className="font-noto text-navy-600 mt-1 text-sm">毎年の固定資産税、何年分払い続けますか？</p>
                    </div>
                  </div>
                  <i className={`ri-arrow-${openSection === 'tax-burden' ? 'up' : 'down'}-s-line text-2xl text-navy-500 transition-transform duration-200`}></i>
                </div>
              </button>

              <div className={`transition-all duration-500 ease-in-out ${
                openSection === 'tax-burden' 
                  ? 'max-h-[10000px] opacity-100' 
                  : 'max-h-0 opacity-0'
              } overflow-hidden`}>
                <div className="px-6 pb-6">
                  {/* シミュレーター */}
                  <div className="bg-gradient-to-br from-teal-50 to-blue-50 rounded-xl p-8 mb-8 border-2 border-teal-200">
                    <div className="text-center mb-6">
                      <h5 className="font-noto text-2xl font-bold text-navy-900 mb-2">
                        あなたの固定資産税でシミュレーション
                      </h5>
                      <p className="font-noto text-navy-600 text-sm">
                        年間の固定資産税額を入力して、放置した場合の損失を確認しましょう
                      </p>
                    </div>

                    {/* 入力エリア */}
                    <div className="bg-white rounded-lg p-6 mb-6 shadow-sm">
                      <label className="block font-noto text-navy-800 font-semibold mb-3">
                        年間固定資産税額を入力してください
                      </label>
                      <div className="flex items-center gap-4">
                        <input
                          type="range"
                          min="1"
                          max="20"
                          step="1"
                          value={annualTax}
                          onChange={(e) => setAnnualTax(Number(e.target.value))}
                          className="flex-1 h-3 bg-teal-200 rounded-lg appearance-none cursor-pointer accent-teal-600"
                        />
                        <div className="bg-teal-600 text-white rounded-lg px-6 py-3 min-w-[140px] text-center">
                          <span className="font-en text-3xl font-bold">{annualTax}</span>
                          <span className="font-noto text-sm ml-1">万円/年</span>
                        </div>
                      </div>
                      <div className="flex justify-between mt-2 text-xs text-navy-500">
                        <span>1万円</span>
                        <span>20万円</span>
                      </div>
                    </div>

                    {/* 結果表示 */}
                    <div className="grid md:grid-cols-2 gap-6">
                      {/* 放置した場合 */}
                      <div className="bg-red-50 rounded-lg p-6 border-2 border-red-200">
                        <div className="flex items-center gap-2 mb-4">
                          <i className="ri-alert-line text-red-600 text-2xl"></i>
                          <h6 className="font-noto text-lg font-bold text-red-800">放置した場合の累積負担</h6>
                        </div>
                        <div className="space-y-3">
                          <div className="flex justify-between items-center">
                            <span className="text-navy-700">10年後</span>
                            <span className="font-en text-xl font-bold text-red-600">{calculateLoss(10)}万円</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-navy-700">20年後</span>
                            <span className="font-en text-xl font-bold text-red-600">{calculateLoss(20)}万円</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-navy-700">30年後</span>
                            <span className="font-en text-xl font-bold text-red-600">{calculateLoss(30)}万円</span>
                          </div>
                        </div>
                      </div>

                      {/* 処分した場合 */}
                      <div className="bg-green-50 rounded-lg p-6 border-2 border-green-200">
                        <div className="flex items-center gap-2 mb-4">
                          <i className="ri-check-line text-green-600 text-2xl"></i>
                          <h6 className="font-noto text-lg font-bold text-green-800">110万円で処分した場合の実質負担</h6>
                        </div>
                        <div className="space-y-3">
                          <div className="flex justify-between items-center">
                            <span className="text-navy-700">10年後</span>
                            <span className="font-en text-xl font-bold text-green-600">
                              {calculateNetCost(10) > 0 ? `${calculateNetCost(10)}万円` : `${Math.abs(calculateNetCost(10))}万円得`}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-navy-700">20年後</span>
                            <span className="font-en text-xl font-bold text-green-600">
                              {calculateNetCost(20) > 0 ? `${calculateNetCost(20)}万円` : `${Math.abs(calculateNetCost(20))}万円得`}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-navy-700">30年後</span>
                            <span className="font-en text-xl font-bold text-green-600">
                              {calculateNetCost(30) > 0 ? `${calculateNetCost(30)}万円` : `${Math.abs(calculateNetCost(30))}万円得`}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* 損益分岐点 */}
                    <div className="mt-6 bg-white rounded-lg p-6 border-2 border-teal-300 text-center">
                      <p className="font-noto text-navy-700 mb-2">
                        <i className="ri-calculator-line text-teal-600 mr-2"></i>
                        損益分岐点
                      </p>
                      <p className="font-noto text-3xl font-bold text-teal-600 mb-2">
                        {breakEvenYears}年後
                      </p>
                      <p className="font-noto text-sm text-navy-600">
                        {breakEvenYears}年以上持ち続けるなら、今すぐ処分した方がお得です
                      </p>
                    </div>
                  </div>

                  <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
                    <p className="text-yellow-800">
                      <strong>⚠️ 固定資産税は一生払い続ける</strong><br />
                      60代の方なら、あと20～30年の負担が確実に発生します。<br />
                      70代の方でも、お子様に引き継がれて負担が続きます。
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* ② 管理費用・維持費用の累積 */}
            <div>
              <button
                onClick={() => toggleSection('maintenance-costs')}
                className="w-full p-6 text-left hover:bg-beige-50 transition-colors duration-200 cursor-pointer"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <span className="bg-navy-800 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3">②</span>
                    <div>
                      <h4 className="font-noto text-xl font-bold text-navy-900">
                        管理費用・維持費用の累積
                      </h4>
                      <p className="font-noto text-navy-600 mt-1 text-sm">草刈り、修繕、管理...「見えないコスト」が積み重なる</p>
                    </div>
                  </div>
                  <i className={`ri-arrow-${openSection === 'maintenance-costs' ? 'up' : 'down'}-s-line text-2xl text-navy-500 transition-transform duration-200`}></i>
                </div>
              </button>

              <div className={`transition-all duration-500 ease-in-out ${
                openSection === 'maintenance-costs' 
                  ? 'max-h-[10000px] opacity-100' 
                  : 'max-h-0 opacity-0'
              } overflow-hidden`}>
                <div className="px-6 pb-6">
                  <p className="font-noto text-navy-700 text-sm mb-6">
                    固定資産税だけではありません。<br />
                    負動産を持ち続けると、以下のような費用が毎年発生します。
                  </p>
                  
                  <div className="bg-white rounded-lg border border-gray-200 overflow-hidden mb-6">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left font-semibold text-gray-800">項目</th>
                          <th className="px-4 py-3 text-left font-semibold text-gray-800">頻度</th>
                          <th className="px-4 py-3 text-left font-semibold text-gray-800">1回あたり費用</th>
                          <th className="px-4 py-3 text-left font-semibold text-gray-800">年間費用</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-t">
                          <td className="px-4 py-3">草刈り</td>
                          <td className="px-4 py-3">年2～3回</td>
                          <td className="px-4 py-3">3～5万円</td>
                          <td className="px-4 py-3 font-semibold text-red-600">6～15万円</td>
                        </tr>
                        <tr className="border-t">
                          <td className="px-4 py-3">樹木の伐採</td>
                          <td className="px-4 py-3">数年に1回</td>
                          <td className="px-4 py-3">10～30万円</td>
                          <td className="px-4 py-3 font-semibold text-red-600">平均5万円/年</td>
                        </tr>
                        <tr className="border-t">
                          <td className="px-4 py-3">建物の簡易修繕</td>
                          <td className="px-4 py-3">随時</td>
                          <td className="px-4 py-3">10～50万円</td>
                          <td className="px-4 py-3 font-semibold text-red-600">平均10万円/年</td>
                        </tr>
                        <tr className="border-t">
                          <td className="px-4 py-3">交通費（現地確認）</td>
                          <td className="px-4 py-3">年1～2回</td>
                          <td className="px-4 py-3">1～3万円</td>
                          <td className="px-4 py-3 font-semibold text-red-600">2～6万円</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-orange-50 rounded-lg p-6">
                      <h6 className="font-bold text-orange-800 mb-3">【山林・農地の場合】</h6>
                      <p className="font-noto text-navy-700 text-sm mb-2">固定資産税 5万円 + 草刈り 10万円 + 交通費 3万円 = 年間18万円</p>
                      <p className="font-noto text-navy-700 text-sm mb-3">10年で180万円、20年で360万円の負担</p>
                      <div className="bg-orange-100 p-3 rounded text-sm font-semibold text-orange-800">
                        → 110万円で処分すれば、7年目以降はずっとお得
                      </div>
                    </div>
                    
                    <div className="bg-red-50 rounded-lg p-6">
                      <h6 className="font-bold text-red-800 mb-3">【老朽化した戸建ての場合】</h6>
                      <p className="font-noto text-navy-700 text-sm mb-2">固定資産税 8万円 + 草刈り 6万円 + 修繕費 10万円 + 交通費 5万円 = 年間29万円</p>
                      <p className="font-noto text-navy-700 text-sm mb-3">5年で145万円、10年で290万円の負担</p>
                      <div className="bg-red-100 p-3 rounded text-sm font-semibold text-red-800">
                        → 110万円で処分すれば、4年目以降はずっとお得
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-red-50 border-l-4 border-red-400 p-4 mt-6">
                    <p className="font-noto text-navy-700 text-sm">
                      <strong>⚠️ 建物は放置すると「特定空家」に指定される可能性</strong><br />
                      特定空家に指定されると：<br />
                      ・固定資産税が最大6倍に増額<br />
                      ・行政代執行で取り壊され、費用を請求される（100～300万円）
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* ③ 相続時の負担増大 */}
            <div>
              <button
                onClick={() => toggleSection('inheritance-burden')}
                className="w-full p-6 text-left hover:bg-beige-50 transition-colors duration-200 cursor-pointer"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <span className="bg-navy-800 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3">③</span>
                    <div>
                      <h4 className="font-noto text-xl font-bold text-navy-900">
                        相続時の負担増大
                      </h4>
                      <p className="font-noto text-navy-600 mt-1 text-sm">相続が発生すると、問題はさらに深刻化します</p>
                    </div>
                  </div>
                  <i className={`ri-arrow-${openSection === 'inheritance-burden' ? 'up' : 'down'}-s-line text-2xl text-navy-500 transition-transform duration-200`}></i>
                </div>
              </button>

              <div className={`transition-all duration-500 ease-in-out ${
                openSection === 'inheritance-burden' 
                  ? 'max-h-[10000px] opacity-100' 
                  : 'max-h-0 opacity-0'
              } overflow-hidden`}>
                <div className="px-6 pb-6">
                  <p className="font-noto text-navy-700 text-sm mb-6">
                    「自分が生きている間は何とかなる」と思っていても、<br />
                    相続が発生すると、お子様・お孫様に大きな負担がかかります。
                  </p>
                  
                  <div className="bg-white rounded-lg border border-gray-200 overflow-hidden mb-6">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left font-semibold text-gray-800">項目</th>
                          <th className="px-4 py-3 text-left font-semibold text-gray-800">費用</th>
                          <th className="px-4 py-3 text-left font-semibold text-gray-800">詳細</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-t">
                          <td className="px-4 py-3">相続登記費用</td>
                          <td className="px-4 py-3 font-semibold text-red-600">5～15万円</td>
                          <td className="px-4 py-3">2024年4月から義務化。怠ると10万円以下の過料</td>
                        </tr>
                        <tr className="border-t">
                          <td className="px-4 py-3">遺産分割協議</td>
                          <td className="px-4 py-3 font-semibold text-red-600">0～数百万円</td>
                          <td className="px-4 py-3">相続人が多い場合、弁護士費用が発生</td>
                        </tr>
                        <tr className="border-t">
                          <td className="px-4 py-3">相続税の計算での不利</td>
                          <td className="px-4 py-3 font-semibold text-red-600">数十～数百万円</td>
                          <td className="px-4 py-3">負動産も「財産」としてカウントされ、他の相続人の税負担増</td>
                        </tr>
                        <tr className="border-t">
                          <td className="px-4 py-3">相続人間の争い</td>
                          <td className="px-4 py-3 font-semibold text-red-600">計り知れない</td>
                          <td className="px-4 py-3">「誰がこの土地を相続するか」で揉める</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h6 className="font-bold text-gray-800 mb-4">【事例】固定資産税5万円の山林を放置した場合</h6>
                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <div className="bg-blue-100 text-blue-600 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mt-1">1</div>
                        <div>
                          <p className="font-semibold">父が所有（70歳）→ 10年後に相続発生</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="bg-blue-100 text-blue-600 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mt-1">2</div>
                        <div>
                          <p className="font-semibold">子ども3人で遺産分割協議</p>
                          <p className="text-sm text-gray-600">「この山林、誰が相続する？」「私は要らない」「僕も要らない」「じゃあ共有にする？」</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="bg-blue-100 text-blue-600 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mt-1">3</div>
                        <div>
                          <p className="font-semibold">3人の共有状態に（誰も管理しない）</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="bg-blue-100 text-blue-600 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mt-1">4</div>
                        <div>
                          <p className="font-semibold">さらに10年後、子どもの一人が死亡</p>
                          <p className="text-sm text-gray-600">→ 孫の世代も含めて共有者が6人に</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="bg-red-100 text-red-600 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mt-1">5</div>
                        <div>
                          <p className="font-semibold text-red-600">もはや処分不可能な「塩漬け資産」に</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-6 p-4 bg-white rounded border-l-4 border-red-400">
                      <p className="font-noto text-navy-700 text-sm mb-2">この間の固定資産税：5万円 × 20年 = 100万円</p>
                      <p className="font-noto text-navy-700 text-sm mb-3">さらに、相続登記費用、税理士費用なども発生</p>
                      <p className="font-semibold text-red-600">
                        → 最初の時点で110万円払って処分していれば、子ども・孫の世代への負担ゼロ
                      </p>
                    </div>
                  </div>
                  
                  <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mt-6">
                    <p className="text-yellow-800">
                      <strong>⚠️ 相続が発生すると「処分のハードル」が一気に上がる</strong><br />
                      ・相続人全員の同意が必要<br />
                      ・連絡が取れない相続人がいると手続き不可<br />
                      ・相続人同士の意見対立で長期化<br /><br />
                      <strong>今なら、あなた一人の判断で処分できます。</strong>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* ④ 精神的負担・機会損失 */}
            <div>
              <button
                onClick={() => toggleSection('psychological-burden')}
                className="w-full p-6 text-left hover:bg-beige-50 transition-colors duration-200 cursor-pointer"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <span className="bg-navy-800 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3">④</span>
                    <div>
                      <h4 className="font-noto text-xl font-bold text-navy-900">
                        精神的負担・機会損失
                      </h4>
                      <p className="font-noto text-navy-600 mt-1 text-sm">お金だけじゃない、「心の負担」も大きい</p>
                    </div>
                  </div>
                  <i className={`ri-arrow-${openSection === 'psychological-burden' ? 'up' : 'down'}-s-line text-2xl text-navy-500 transition-transform duration-200`}></i>
                </div>
              </button>

              <div className={`transition-all duration-500 ease-in-out ${
                openSection === 'psychological-burden' 
                  ? 'max-h-[10000px] opacity-100' 
                  : 'max-h-0 opacity-0'
              } overflow-hidden`}>
                <div className="px-6 pb-6">
                  <p className="font-noto text-navy-700 text-sm mb-6">負動産を持ち続けることで失われるもの：</p>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-red-50 rounded-lg p-6">
                      <h6 className="font-bold text-red-800 mb-4">【精神的負担】</h6>
                      <div className="space-y-2">
                        <div className="flex items-start gap-2">
                          <span className="text-red-500 mt-1">✗</span>
                          <span className="text-gray-700">「また草刈りの時期か...」という憂鬱</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <span className="text-red-500 mt-1">✗</span>
                          <span className="text-gray-700">近隣から苦情が来ないかという不安</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <span className="text-red-500 mt-1">✗</span>
                          <span className="text-gray-700">「子どもに迷惑をかけてしまう」という罪悪感</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <span className="text-red-500 mt-1">✗</span>
                          <span className="text-gray-700">「いつか処分しなきゃ」と思いながら先延ばし</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-blue-50 rounded-lg p-6">
                      <h6 className="font-bold text-blue-800 mb-4">【機会損失】</h6>
                      <div className="space-y-3">
                        <div>
                          <div className="flex items-start gap-2 mb-1">
                            <span className="text-red-500 mt-1">✗</span>
                            <span className="text-gray-700 font-semibold">毎年の固定資産税・管理費に使うお金</span>
                          </div>
                          <p className="text-sm text-gray-600 ml-4">→ 旅行、趣味、孫へのプレゼント、老後資金に使えたはず</p>
                        </div>
                        <div>
                          <div className="flex items-start gap-2 mb-1">
                            <span className="text-red-500 mt-1">✗</span>
                            <span className="text-gray-700 font-semibold">草刈りや現地確認に使う時間</span>
                          </div>
                          <p className="text-sm text-gray-600 ml-4">→ 家族との時間、趣味の時間に使えたはず</p>
                        </div>
                        <div>
                          <div className="flex items-start gap-2 mb-1">
                            <span className="text-red-500 mt-1">✗</span>
                            <span className="text-gray-700 font-semibold">「この負動産をどうしよう」と悩む時間</span>
                          </div>
                          <p className="text-sm text-gray-600 ml-4">→ 安心して過ごせる時間に使えたはず</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-green-50 border-l-4 border-green-400 p-6 mt-6 text-center">
                    <p className="font-noto text-navy-800 text-xl font-bold">
                      110万円で、これらすべてから解放されます。
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
